"use client";

import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="204704" className="flex gap-4">
        {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-6 w-20 bg-muted/20 animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList>
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="text-muted-foreground hover:text-primary transition-colors font-default font-medium text-sm px-4 py-2 tracking-tight">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onItemClick
}: {
  onItemClick: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 px-4 py-6">
        {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-10 w-full bg-muted/20 animate-pulse rounded-md" />)}
      </div>;
  }
  return <div className="flex flex-col px-4 py-4 gap-2 bg-background">
      {menuItems.map((item, i) => <div key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="block py-3 text-base font-default font-medium text-foreground border-b border-border/20 hover:text-primary transition-colors" onClick={onItemClick}>
            {item.label}
          </Link>
        </div>)}
      <div className="pt-4 pb-2">
        <Button className="w-full font-default font-bold bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_15px_rgba(191,255,0,0.3)]" size="lg">
          Get Started
        </Button>
      </div>
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="header" className={`sticky top-0 z-50 w-full transition-all duration-300 dark bg-background text-foreground ${isScrolled ? "bg-background/80 backdrop-blur-lg border-b border-border/20 shadow-lg" : "bg-background border-b border-transparent"}`}>
      <div className="container mx-auto px-4 lg:px-8 h-20 flex items-center justify-between">
        {}
        <div className="flex-shrink-0 flex items-center">
          <Link to="#">
            <img src="https://bursting-osprey.10web.cloud/wp-content/uploads/2026/04/Gemini_Generated_Image_az4cf1az4cf1az4c-SMALL-scaled.png" alt="AGS Partners" className="w-auto object-contain !h-[80px]" />
          </Link>
        </div>

        {}
        <div className="hidden md:flex flex-1 justify-center">
          <MenuProvider menu_id="42">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="hidden md:flex items-center justify-end flex-shrink-0">
          <Button asChild className="font-default font-bold px-6 bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-300 hover:scale-[1.02] shadow-[0_0_10px_rgba(191,255,0,0.2)]">
            <Link to="https://links.agssuite.com/widget/form/0kYf5fvvvIeS2gTSy86O" newTab>
              Get Started
            </Link>
          </Button>
        </div>

        {}
        <div className="flex md:hidden items-center justify-end flex-shrink-0">
          <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Toggle Menu" className="text-foreground hover:bg-muted/10">
                <Menu className="size-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="top" className="w-full p-0 pt-12 bg-background border-b border-border/20">
              <SheetHeader className="sr-only">
                <SheetTitle>Navigation Menu</SheetTitle>
              </SheetHeader>
              <MenuProvider menu_id="42">
                <MobileMenuConsumer onItemClick={() => setIsMobileMenuOpen(false)} />
              </MenuProvider>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </section>;
}